import { Injectable, Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import { SEARCH_LINK_UNREADABLE_MESSAGE, searchLinkModules, warnSearchLink } from '@core/misc/search-link.utils';
import { authInit } from '@core/modules/auth/state/auth.actions';
import { SessionCheckService } from '@core/modules/session-check/session-check.service';
import { StaleBundleService } from '@core/modules/stale-bundle/stale-bundle.service';
import { environment } from '@env/environment';
import { Navigate } from '@ngxs/router-plugin';
import { createDispatchMap, select } from '@ngxs/store';
import { aviaReset } from '@pages/avia-page/state/avia.actions';
import { hotelsReset } from '@pages/hotels-page/state/hotels.actions';
import { reservationsReset } from '@pages/reservations-page/state/reservations.action';
import { toursReset } from '@pages/tours-page/state/tours.actions';
import { chatReset } from '@shared/modules/chat/state/chat.actions';
import { appInit, appSetSearchByUrl } from '@app-state/app.actions';
import { AppPersistedState } from '@app-state/app.persisted.state';
import { DeviceService } from './device.service';
import { LocaleService } from './locale.service';
import { ScreenSizeService } from './screen-size.service';
import { SvgIconsService } from './svg-icons.service';
import { TitleService } from './title.service';
import { WebsiteFlagsService } from './website-flags.service';
import { AgencyDefaultsService } from './agency-defaults.service';

type SearchByUrlModule = 'tours' | 'hotels';

const SEARCH_URL_PARAM = 'search';
const BASE64_PAYLOAD_URL_PARAM = 'base64Payload';
/** The search tabs a link may open (R-SRF8d); only those of PAYLOAD_SEARCH_MODULES read the payload, the others open empty (R-AVF15). */
const SEARCH_LINK_TABS: readonly string[] = ['tours', 'hotels', 'avia', 'excursions'];
const PAYLOAD_SEARCH_MODULES: readonly string[] = ['tours', 'hotels'];
const CLEAR_URL_SEARCH_PARAMS = { search: null, base64Payload: null };

@Injectable({
  providedIn: 'root'
})
export class StartupService {
  readonly #localeService = inject(LocaleService);
  readonly #svgIconsService = inject(SvgIconsService);
  readonly #deviceService = inject(DeviceService);
  readonly #titleService = inject(TitleService);
  readonly #sessionCheckService = inject(SessionCheckService);
  readonly #staleBundleService = inject(StaleBundleService);
  readonly #screenSizeService = inject(ScreenSizeService);
  readonly #websiteFlagsService = inject(WebsiteFlagsService);
  readonly #agencyDefaultsService = inject(AgencyDefaultsService);
  readonly #router = inject(Router);
  readonly #injector = inject(Injector);

  readonly #language = select(AppPersistedState.language);

  readonly #actions = createDispatchMap({
    authInit: authInit,
    appInit: appInit,
    toursReset: toursReset,
    hotelsReset: hotelsReset,
    aviaReset: aviaReset,
    reservationsReset: reservationsReset,
    chatReset: chatReset,
    navigate: Navigate,
    setSearchByUrl: appSetSearchByUrl
  });

  /** Bootstraps every application-level service and dispatches the initial store actions. */
  public run(): void {
    this.#screenSizeService.init();
    this.#titleService.init();
    this.#deviceService.init();
    this.#svgIconsService.init();
    this.#localeService.init(this.#language());
    this.#sessionCheckService.init();
    this.#staleBundleService.init();
    this.#websiteFlagsService.init(); // Kazunion Website Flags (R-WF12): the agent's flags while signed in
    this.#agencyDefaultsService.init(); // R-CAL17: the agency's default currency, once per sign-in
    this.#checkSearchByUrlParams();
    this.#runStoreActions();

    if (environment.resetStatesBeforeUnload) this.#resetStatesBeforeUnload();
  }

  #runStoreActions(): void {
    this.#actions.authInit();
    this.#actions.appInit();
  }

  #resetStatesBeforeUnload(): void {
    window.addEventListener('beforeunload', () => {
      this.#actions.toursReset();
      this.#actions.hotelsReset();
      this.#actions.aviaReset();
      this.#actions.reservationsReset();
      this.#actions.chatReset();
    });
  }

  /**
   * Search links - docs features/website/search-forms.md R-SRF8 / R-SRF8c / R-SRF8d (docs#446): `search` must be a module of the allow-list
   * derived from the router configuration (anything else opens the start page and stops here - before, it went on to navigate to the
   * text of the link); a payload that cannot be read opens that module's empty form with a warning.
   */
  #checkSearchByUrlParams(): void {
    const params = new URLSearchParams(window.location.search);
    const search = params.get(SEARCH_URL_PARAM);
    const base64Payload = params.get(BASE64_PAYLOAD_URL_PARAM);
    if (!search || !base64Payload) return;

    if (!searchLinkModules(this.#router.config, SEARCH_LINK_TABS).includes(search)) {
      console.warn('Search link refused: the module is not on the allow-list', { search });
      this.#actions.navigate(['/'], CLEAR_URL_SEARCH_PARAMS);
      return;
    }

    if (PAYLOAD_SEARCH_MODULES.includes(search)) this.#dispatchSearchByUrl(search, base64Payload);
    this.#actions.navigate([search], CLEAR_URL_SEARCH_PARAMS);
  }

  #dispatchSearchByUrl(search: string, base64Payload: string): void {
    try {
      const payload = JSON.parse(atob(base64Payload));
      if (!payload || typeof payload !== 'object' || Array.isArray(payload))
        throw new Error('The payload is not an object');
      this.#actions.setSearchByUrl(search as SearchByUrlModule, payload);
    } catch (error) {
      console.error("Can't parse `base64Payload` from url search params", { search, error });
      warnSearchLink(this.#injector, SEARCH_LINK_UNREADABLE_MESSAGE);
    }
  }
}
